import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import {
    Modal as HerouiModal,
    ModalContent as HerouiModalContent,
    ModalBody as HerouiModalBody,
    ModalHeader as HerouiModalHeader,
    HeroUIProvider,
} from '@heroui/react';
import Button from 'Button';
import { memo, useCallback, useMemo, useState, type FC, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { type ModalProps } from './Modal.types';
import './locales/Modal/Modal-en.json';

const defaultEase: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

// FIXME: see https://github.com/heroui-inc/heroui/issues/2297
const motionProps = (fadeInDuration = 0.4) => ({
    variants: {
        enter: {
            scale: 1,
            y: 'var(--slide-enter)',
            opacity: 1,
            transition: {
                scale: {
                    duration: 0.4,
                    ease: defaultEase,
                },
                opacity: {
                    duration: fadeInDuration,
                    ease: defaultEase,
                },
                y: {
                    type: 'spring' as const,
                    bounce: 0,
                    duration: 0.6,
                },
            },
        },
        exit: {
            scale: 1, // NextUI default 1.03
            y: 'var(--slide-exit)',
            opacity: 0,
            transition: {
                duration: 0.3,
                ease: defaultEase,
            },
        },
    },
});

const CloseButton: FC<{ onClose?: () => void; transparent?: boolean }> = ({ onClose, transparent }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'Modal.Modal' });

    return (
        <Button
            isIconOnly
            aria-label={t('close')}
            onPress={onClose}
            variant="light"
            round
            className={twMerge(
                'absolute right-5 top-7 rtl:left-5 rtl:right-auto',
                transparent ? '-right-9 top-0 bg-white rtl:-left-9 ' : '',
            )}
            height={8}
        >
            <FontAwesomeIcon icon={faXmark} className="text-lg" />
        </Button>
    );
};

const modalBaseSlots = (
    scrollBehavior: 'normal' | 'outside',
    noSize = false,
    titleAlignment = 'center',
    transparent = false,
) => ({
    backdrop: ['bg-black bg-opacity-50 px-2 pt-[55px] backdrop-blur-sm sm:pt-[75px] z-[9999]'],
    wrapper: ['z-[10000]'],
    base: [
        'rounded-8 mx-2.5',
        noSize ? 'min-w-0 max-w-fit w-unset' : '',
        transparent ? 'overflow-visible bg-transparent p-0' : 'overflow-hidden',
    ],
    header: [
        'p-7.5 text-xl font-semibold leading-none text-black',
        titleAlignment === 'start' ? 'justify-start' : 'justify-center',
    ],
    body: ['gap-0 p-0 max-h-[75vh]', scrollBehavior === 'normal' ? 'overflow-y-auto' : 'max-h-none overflow-visible'],
    footer: [''],
    closeButton: [''],
});

export const Modal: FC<ModalProps> = memo(
    ({
        children,
        classNames,
        dismissOnEscape = true,
        dismissOnOverlayClick = true,
        duration = 0.4,
        hideCloseButton = false,
        onClose,
        scrollBehavior = 'normal',
        shadow = 'none',
        size = 'xl',
        title,
        titleAlignment = 'center',
        titleKey,
        transparent = false,
    }) => {
        const [isOpen, setIsOpen] = useState(true);
        const { t } = useTranslation('back_royal');

        const titleText = titleKey ? t(titleKey) : title;

        const handleClose = useCallback(() => {
            setIsOpen(false);
            setTimeout(() => {
                // ensure the animation finishes
                onClose?.();
            }, 300);
        }, [onClose]);

        const slots = useMemo(() => {
            if (!classNames) return modalBaseSlots(scrollBehavior, size === 'unset', titleAlignment, transparent);

            return Object.entries(classNames).reduce(
                (prev, [key, value]) => ({
                    ...prev,
                    [key]: twMerge([prev[key as keyof typeof prev], value]),
                }),
                modalBaseSlots(scrollBehavior, size === 'unset', titleAlignment, transparent) as typeof classNames,
            );
        }, [classNames, scrollBehavior, size, titleAlignment, transparent]);

        return (
            <HeroUIProvider className="hidden">
                <HerouiModal
                    backdrop="blur"
                    classNames={slots}
                    closeButton={<CloseButton onClose={handleClose} transparent={transparent} />}
                    hideCloseButton={hideCloseButton}
                    isDismissable={dismissOnOverlayClick}
                    isKeyboardDismissDisabled={!dismissOnEscape}
                    isOpen={isOpen}
                    motionProps={motionProps(duration)}
                    onClose={handleClose}
                    placement="center"
                    scrollBehavior={scrollBehavior}
                    shadow={shadow}
                    size={size === 'unset' ? undefined : size}
                >
                    <HerouiModalContent>
                        <>
                            {titleText && <HerouiModalHeader>{titleText}</HerouiModalHeader>}
                            <HerouiModalBody>
                                {
                                    (typeof children === 'function'
                                        ? children({ closeModal: handleClose })
                                        : children) as ReactNode
                                }
                            </HerouiModalBody>
                        </>
                    </HerouiModalContent>
                </HerouiModal>
            </HeroUIProvider>
        );
    },
);
